/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Lib dependencies
import NiceModal from '@ebay/nice-modal-react';
import { useNavigate } from '@tanstack/react-router';
import {
  ClipboardCheck,
  ClipboardList,
  MoreHorizontal,
  User,
  Clock,
  Trash,
  Video,
  Download,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Components
import { Button } from '@/components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/UI/Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/UI/DropdownMenu';
import UpdateMeetingModal from './UpdateMeetingModal';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';

// Helpers
import { formatDate } from '@/helpers/date.helper';

// Types
import { Meeting, MeetingStatus } from '@/types/meeting';

// Hooks
import { useUpdateMeeting } from '../hooks/update-meeting.hook';
import { useDownloadReport } from '../hooks/download-report.hook';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
type MeetingCardProps = {
  meeting: Meeting;
};

const MeetingCard = ({ meeting }: MeetingCardProps) => {
  const { day, dayName, month, year } = formatDate(meeting.meeting_date);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { mutate: updateMeeting, isPending } = useUpdateMeeting({
    onSuccess: () => {
      NiceModal.hide(ConfirmationModal);
    },
  });

  const handleCancelMeeting = (id: string) => {
    NiceModal.show(ConfirmationModal, {
      title: t('pages.meetings.cancel_meeting_confirmation'),
      description: `${t('pages.meetings.are_you_sure_you_want_to_cancel_meeting')}`,
      confirmBtnText: t('global.confirm'),
      isLoading: isPending,
      variant: 'destructive',
      onSubmit: () => {
        updateMeeting({
          id,
          data: {
            status: MeetingStatus.CANCELED,
          },
        });
      },
    });
  };

  const handleResumeMeeting = () => {
    NiceModal.show(ConfirmationModal, {
      title: t('pages.meetings.resume_meeting_confirmation'),
      description: `${t('pages.meetings.are_you_sure_you_want_to_resume_meeting')}`,
      confirmBtnText: t('global.confirm'),
      onSubmit: () => {
        window.open(`/meetings/${meeting.id}`, '_blank');
        NiceModal.hide(ConfirmationModal);
      },
    });
  };

  const { mutate: downloadReport, isPending: isDownloadingPdf } =
    useDownloadReport({
      id: meeting.id,
    });

  const handleDownloadReport = () => {
    downloadReport();
  };

  return (
    <Card className="w-full max-w-sm bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 ease-in-out">
      <div className="flex h-full">
        <div
          className={`${
            meeting.status === MeetingStatus.COMING ||
            meeting.status === MeetingStatus.IN_PROGRESS ||
            meeting.status === MeetingStatus.ON_ERROR
              ? 'bg-primary'
              : meeting.status === MeetingStatus.CANCELED
                ? 'bg-destructive text-white'
                : 'bg-background-1 text-white'
          } p-4 flex flex-col items-center justify-center min-w-[80px]`}
        >
          <span className="text-lg ">{dayName}</span>
          <span className="text-3xl font-semibold">{day}</span>
          <span className="text-sm">{month}</span>
          <span className="text-xs mt-1">{year}</span>
        </div>
        <div className="flex-1 flex flex-col">
          <CardHeader className="p-4 flex-1">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <CardTitle className="text-xl font-semibold text-gray-900">
                  {meeting.beneficiary.first_name +
                    ' ' +
                    meeting.beneficiary.last_name}
                </CardTitle>
                <p className="text-gray-600">{meeting.start_at.slice(0, 5)}</p>
                {meeting.status === MeetingStatus.DONE && (
                  <div className="flex items-center mt-2">
                    {meeting.is_repport_done ? (
                      <>
                        <ClipboardCheck className="h-4 w-4 text-primary mr-1" />
                        <span className="text-sm text-primary">
                          {t('pages.meetings.report_done')}
                        </span>
                      </>
                    ) : (
                      <>
                        <ClipboardList className="h-4 w-4 text-secondary mr-1" />
                        <span className="text-sm text-secondary">
                          {t('pages.meetings.report_pending')}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-gray-500 hover:text-gray-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate({
                          to: '/beneficiaries/$id',
                          params: {
                            id: meeting.beneficiary.id,
                          },
                        });
                      }}
                    >
                      <User className="mr-2 h-4 w-4" />
                      {t('pages.meetings.view_beneficiary_details')}
                    </DropdownMenuItem>
                    {meeting.status === MeetingStatus.DONE && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `/meetings/${meeting.id}/report`,
                            '_blank'
                          );
                        }}
                      >
                        {meeting.is_repport_done ? (
                          <ClipboardCheck className="mr-2 h-4 w-4" />
                        ) : (
                          <ClipboardList className="mr-2 h-4 w-4" />
                        )}
                        {meeting.is_repport_done
                          ? t('pages.meetings.view_report')
                          : t('pages.meetings.fill_report')}
                      </DropdownMenuItem>
                    )}
                    {meeting.status !== MeetingStatus.DONE && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          NiceModal.show(UpdateMeetingModal, { meeting });
                        }}
                      >
                        <Clock className="mr-2 h-4 w-4" />
                        {t('global.edit')}
                      </DropdownMenuItem>
                    )}
                    {meeting.status === MeetingStatus.COMING && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelMeeting(meeting.id);
                        }}
                        className="text-destructive"
                      >
                        <Trash className="mr-2 h-4 w-4" />
                        {t('global.cancel')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardHeader>
          {(meeting.status === MeetingStatus.IN_PROGRESS ||
            meeting.status === MeetingStatus.ON_ERROR) && (
            <CardContent className="p-4 bg-gray-50">
              <Button
                className="w-full bg-background-1 hover:bg-background-1/90 text-white font-medium py-2 px-4 rounded-full transition-colors duration-200"
                onClick={handleResumeMeeting}
              >
                <Video className="mr-2 h-4 w-4" />
                {t('global.resume')}
              </Button>
            </CardContent>
          )}
          {meeting.status === MeetingStatus.COMING && (
            <CardContent className="p-4 bg-gray-50">
              <a href={`/meetings/${meeting.id}`} target="_blank">
                <Button className="w-full bg-background-1 hover:bg-background-1/90 text-white font-medium py-2 px-4 rounded-full transition-colors duration-200">
                  <Video className="mr-2 h-4 w-4" />
                  {t('global.join')}
                </Button>
              </a>
            </CardContent>
          )}
          {meeting.status === MeetingStatus.DONE && meeting.is_repport_done && (
            <CardContent className="p-4 bg-gray-50">
              <Button
                className="w-full bg-background-1 hover:bg-background-1/90 text-white font-medium py-2 px-4 rounded-full transition-colors duration-200"
                onClick={handleDownloadReport}
                isLoading={isDownloadingPdf}
              >
                <Download className="mr-2 h-4 w-4" />
                {t('global.download_report')}
              </Button>
            </CardContent>
          )}
        </div>
      </div>
    </Card>
  );
};

export { MeetingCard };
